'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle } from 'lucide-react';

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: 'en' | 'ar';
  prefillPartNumber?: string;
}

interface FormData {
  fullName: string;
  companyName: string;
  email: string;
  phone: string;
  partNumber: string;
  quantity: string;
  message: string;
}

interface FormErrors {
  fullName?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  partNumber?: string;
  quantity?: string;
}

export default function QuoteModal({ isOpen, onClose, language, prefillPartNumber }: QuoteModalProps) {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    partNumber: prefillPartNumber || '',
    quantity: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');

  useEffect(() => {
    if (prefillPartNumber) {
      setFormData((prev) => ({ ...prev, partNumber: prefillPartNumber }));
    }
  }, [prefillPartNumber]);

  useEffect(() => {
    if (isOpen) {
      setSubmitted(false);
      setErrors({});
    }
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = language === 'ar' ? 'الاسم مطلوب' : 'Full name is required';
    if (!formData.companyName.trim()) newErrors.companyName = language === 'ar' ? 'اسم الشركة مطلوب' : 'Company name is required';
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = language === 'ar' ? 'بريد إلكتروني صحيح مطلوب' : 'Valid email is required';
    if (!formData.phone.trim()) newErrors.phone = language === 'ar' ? 'رقم الهاتف مطلوب' : 'Phone number is required';
    if (!formData.partNumber.trim()) newErrors.partNumber = language === 'ar' ? 'رقم القطعة مطلوب' : 'Part number is required';
    if (!formData.quantity.trim()) newErrors.quantity = language === 'ar' ? 'الكمية مطلوبة' : 'Quantity is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const ref = `PLC-${Date.now().toString().slice(-6)}`;
    setReferenceNumber(ref);
    setSubmitted(true);
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  if (!isOpen) return null;

  const dir = language === 'ar' ? 'rtl' : 'ltr';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={language === 'ar' ? 'طلب عرض سعر' : 'Request a Quote'}
      dir={dir}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Card */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-slide-up">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div>
            <h2 className="text-lg font-bold text-onyx font-space-grotesk">
              {language === 'ar' ? 'طلب عرض سعر' : 'Request a Quote'}
            </h2>
            <p className="text-xs text-slate-grey font-roboto mt-0.5">
              {language === 'ar' ? 'سيرد فريقنا عليك خلال 30 دقيقة' : 'Our team will respond within 30 minutes'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors duration-200 text-slate-500 hover:text-onyx"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-6">
          {submitted ? (
            /* Success State */
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-iraqi-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-iraqi-green" />
              </div>
              <h3 className="text-xl font-bold text-onyx font-space-grotesk mb-3">
                {language === 'ar' ? 'شكراً لك!' : 'Thank you!'}
              </h3>
              <p className="text-slate-grey font-roboto text-sm leading-relaxed mb-4">
                {language === 'ar' ?'سيرسل فريقنا عرض السعر خلال 30 دقيقة خلال ساعات العمل.' :'Thank you! Our team will send you a quote within 30 minutes during business hours.'}
              </p>
              <div className="bg-light-grey border border-slate-200 rounded-lg px-4 py-3 mb-6 inline-block">
                <span className="text-xs text-slate-grey font-roboto">
                  {language === 'ar' ? 'رقم المرجع: ' : 'Reference: '}
                </span>
                <span className="text-sm font-bold text-onyx font-mono">{referenceNumber}</span>
              </div>
              <button
                onClick={onClose}
                className="block w-full bg-iraqi-red hover:bg-primary-dark text-white font-semibold py-3 rounded-lg transition-all duration-200 font-space-grotesk"
              >
                {language === 'ar' ? 'إغلاق' : 'Close'}
              </button>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Full Name */}
                <div>
                  <label className="block text-xs font-semibold text-onyx font-space-grotesk mb-1.5">
                    {language === 'ar' ? 'الاسم الكامل' : 'Full Name'} <span className="text-iraqi-red">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm font-roboto focus:outline-none focus:ring-2 focus:ring-iraqi-red/30 transition-all duration-200 ${
                      errors.fullName ? 'border-red-400 bg-red-50' : 'border-slate-200 focus:border-iraqi-red'
                    }`}
                    placeholder={language === 'ar' ? 'محمد أحمد' : 'John Smith'}
                    aria-required="true"
                  />
                  {errors.fullName && <p className="text-xs text-red-500 mt-1 font-roboto">{errors.fullName}</p>}
                </div>

                {/* Company Name */}
                <div>
                  <label className="block text-xs font-semibold text-onyx font-space-grotesk mb-1.5">
                    {language === 'ar' ? 'اسم الشركة' : 'Company Name'} <span className="text-iraqi-red">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm font-roboto focus:outline-none focus:ring-2 focus:ring-iraqi-red/30 transition-all duration-200 ${
                      errors.companyName ? 'border-red-400 bg-red-50' : 'border-slate-200 focus:border-iraqi-red'
                    }`}
                    placeholder={language === 'ar' ? 'شركة البترول العراقية' : 'Basra Oil Company'}
                    aria-required="true"
                  />
                  {errors.companyName && <p className="text-xs text-red-500 mt-1 font-roboto">{errors.companyName}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Email */}
                <div>
                  <label className="block text-xs font-semibold text-onyx font-space-grotesk mb-1.5">
                    {language === 'ar' ? 'البريد الإلكتروني' : 'Email Address'} <span className="text-iraqi-red">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm font-roboto focus:outline-none focus:ring-2 focus:ring-iraqi-red/30 transition-all duration-200 ${
                      errors.email ? 'border-red-400 bg-red-50' : 'border-slate-200 focus:border-iraqi-red'
                    }`}
                    placeholder="engineer@company.com"
                    aria-required="true"
                  />
                  {errors.email && <p className="text-xs text-red-500 mt-1 font-roboto">{errors.email}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs font-semibold text-onyx font-space-grotesk mb-1.5">
                    {language === 'ar' ? 'رقم الهاتف' : 'Phone Number'} <span className="text-iraqi-red">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm font-roboto focus:outline-none focus:ring-2 focus:ring-iraqi-red/30 transition-all duration-200 ${
                      errors.phone ? 'border-red-400 bg-red-50' : 'border-slate-200 focus:border-iraqi-red'
                    }`}
                    placeholder="+964 7XX XXX XXXX"
                    aria-required="true"
                  />
                  {errors.phone && <p className="text-xs text-red-500 mt-1 font-roboto">{errors.phone}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Part Number */}
                <div>
                  <label className="block text-xs font-semibold text-onyx font-space-grotesk mb-1.5">
                    {language === 'ar' ? 'رقم القطعة' : 'Part Number'} <span className="text-iraqi-red">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.partNumber}
                    onChange={(e) => handleChange('partNumber', e.target.value)}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm font-roboto font-mono focus:outline-none focus:ring-2 focus:ring-iraqi-red/30 transition-all duration-200 ${
                      errors.partNumber ? 'border-red-400 bg-red-50' : 'border-slate-200 focus:border-iraqi-red'
                    }`}
                    placeholder="6ES7214-1AG40-0XB0"
                    aria-required="true"
                  />
                  {errors.partNumber && <p className="text-xs text-red-500 mt-1 font-roboto">{errors.partNumber}</p>}
                </div>

                {/* Quantity */}
                <div>
                  <label className="block text-xs font-semibold text-onyx font-space-grotesk mb-1.5">
                    {language === 'ar' ? 'الكمية' : 'Quantity'} <span className="text-iraqi-red">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => handleChange('quantity', e.target.value)}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm font-roboto focus:outline-none focus:ring-2 focus:ring-iraqi-red/30 transition-all duration-200 ${
                      errors.quantity ? 'border-red-400 bg-red-50' : 'border-slate-200 focus:border-iraqi-red'
                    }`}
                    placeholder="1"
                    aria-required="true"
                  />
                  {errors.quantity && <p className="text-xs text-red-500 mt-1 font-roboto">{errors.quantity}</p>}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-semibold text-onyx font-space-grotesk mb-1.5">
                  {language === 'ar' ? 'متطلبات إضافية' : 'Additional Requirements'}
                  <span className="text-slate-400 font-normal ml-1">({language === 'ar' ? 'اختياري' : 'optional'})</span>
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm font-roboto focus:outline-none focus:ring-2 focus:ring-iraqi-red/30 focus:border-iraqi-red transition-all duration-200 resize-none"
                  placeholder={language === 'ar' ? 'أي متطلبات خاصة، مواعيد التسليم، أو تفاصيل المشروع...' : 'Any special requirements, delivery timeline, or project details...'}
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="w-full bg-iraqi-red hover:bg-primary-dark text-white font-semibold py-3.5 rounded-lg transition-all duration-200 hover:shadow-lg font-space-grotesk text-sm mt-2"
              >
                {language === 'ar' ? 'إرسال طلب عرض السعر' : 'Submit Quote Request'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
